import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TargetIcon, HomeIcon, CircleDotIcon, PenLineIcon, PlusIcon, SearchIcon, MapPinIcon, XIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { RADIUS_OPTIONS, ALL_SECTORS, sectorsFor, addressesFor, type AddressEntry, type Coord } from '../../data/communications';
import { useCity } from '../../contexts/CityContext';
import { getPlaceLabel } from '../../utils/audience';
import type { AudiencePlace, AudiencePlaceKind } from '../../types/composer';
const PLACE_ICONS: Record<AudiencePlaceKind, typeof TargetIcon> = {
  sector: TargetIcon,
  address: HomeIcon,
  radius: CircleDotIcon,
  area: PenLineIcon
};
type AddMode = 'sector' | 'address' | 'area';
interface AudiencePlacesProps {
  places: AudiencePlace[];
  onChange: (places: AudiencePlace[]) => void;
  onToggleSector: (sectorId: string) => void;
  drawing: boolean;
  draftPoints: Coord[];
  onStartDrawing: () => void;
  onCancelDrawing: () => void;
  onFinishDrawing: () => void;
}

/**
 * "Lieu" builder: added places as chips, and a single adder that only
 * opens once the user picks what kind of place to add.
 */
export function AudiencePlaces({
  places,
  onChange,
  onToggleSector,
  drawing,
  draftPoints,
  onStartDrawing,
  onCancelDrawing,
  onFinishDrawing
}: AudiencePlacesProps) {
  const {
    t,
    lang
  } = useLanguage();
  const {
    city
  } = useCity();
  const citySectors = sectorsFor(city);
  const cityAddresses = addressesFor(city);
  const [mode, setMode] = useState<AddMode | null>(null);

  // Address adder
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<AddressEntry | null>(null);
  const [radius, setRadius] = useState<string>('exact');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const matches = query ? cityAddresses.filter(a => a.label.toLowerCase().includes(query.toLowerCase())) : cityAddresses;
  const isAddressUsed = (label: string) => places.some(p => p.kind === 'address' && p.label === label && radius === 'exact');
  const addAddress = () => {
    if (!picked) return;
    const id = `pl-${Date.now()}`;
    const place: AudiencePlace = radius === 'exact' ? {
      id,
      kind: 'address',
      label: picked.label,
      coord: picked.coord
    } : {
      id,
      kind: 'radius',
      label: picked.label,
      coord: picked.coord,
      radius
    };
    onChange([...places, place]);
    setQuery('');
    setPicked(null);
  };
  const closeAdder = () => {
    if (drawing) onCancelDrawing();
    setMode(null);
    setQuery('');
    setPicked(null);
  };
  const remove = (id: string) => onChange(places.filter(p => p.id !== id));
  const selectedSectors = places.filter((p): p is Extract<AudiencePlace, {
    kind: 'sector';
  }> => p.kind === 'sector').map(p => p.sectorId);
  const modes: {
    id: AddMode;
    icon: typeof TargetIcon;
    label: string;
  }[] = [{
    id: 'sector',
    icon: TargetIcon,
    label: t('Secteur', 'District')
  }, {
    id: 'address',
    icon: HomeIcon,
    label: t('Adresse', 'Address')
  }, {
    id: 'area',
    icon: PenLineIcon,
    label: t('Zone sur la carte', 'Area on map')
  }];
  return <div className="space-y-3" data-id="element-3788" data-mp="3pxposak62ceg">
      {/* Places list */}
      {places.length > 0 && <ul className="flex flex-wrap gap-2" aria-label={t('Lieux ajoutés', 'Added places')} data-id="element-3789" data-mp="3tz1h4nufdbqc">
          <AnimatePresence initial={false} data-id="element-3790" data-mp="3g14fve510h5c">
            {places.map((place, index) => {
          const Icon = PLACE_ICONS[place.kind];
          const color = place.kind === 'sector' ? ALL_SECTORS.find(s => s.id === place.sectorId)?.color : undefined;
          return <motion.li key={place.id} layout initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.12
          }} className="inline-flex items-center gap-2 max-w-full" data-id="element-3791" data-mp="2219sgp2da9sq" data-mpi={index}>
                  {index > 0 && <span className="text-[11px] font-semibold uppercase text-slate-400" data-id="element-3792" data-mp="1ietx72lmx9o1">
                      {t('ou', 'or')}
                    </span>}
                  <span className="inline-flex items-center gap-2 pl-2.5 pr-1 py-1 rounded-lg border border-slate-200 bg-white text-sm max-w-full" data-id="element-3793" data-mp="1s453khh50088">
                  <span className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 text-white" style={{
                backgroundColor: color ?? '#4F46E5'
              }} data-id="element-3794" data-mp="1nbbwhmy74fsz">
                    <Icon className="w-3 h-3" aria-hidden="true" data-id="element-3795" data-mp="um4z4rah9w9b" />
                  </span>
                  <span className="font-medium text-slate-700 truncate" data-id="element-3796" data-mp="2uocz4s3sfd1z">
                    {getPlaceLabel(place)[lang]}
                  </span>
                  <button type="button" onClick={() => remove(place.id)} aria-label={`${t('Retirer', 'Remove')} ${getPlaceLabel(place)[lang]}`} className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-id="element-3797" data-mp="34fqvws4ctyrf">
                    <XIcon className="w-3.5 h-3.5" data-id="element-3798" data-mp="2gr7tuubwei7s" />
                  </button>
                  </span>
                </motion.li>;
        })}
          </AnimatePresence>
        </ul>}

      {/* Adder — closed by default: just three compact "+" buttons */}
      {mode === null && places.length > 0 ? <div className="relative w-fit" ref={menuRef} data-id="element-3799" data-mp="11wehayqcy1i">
          <button type="button" onClick={() => setMenuOpen(o => !o)} aria-expanded={menuOpen} aria-haspopup="menu" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-[#4F46E5] rounded-lg hover:bg-indigo-50 transition-colors" data-id="element-3800" data-mp="4zu58i85fs70">
            <PlusIcon className="w-4 h-4" data-id="element-3801" data-mp="1gorlcg1v908w" />
            {t('Ajouter un lieu', 'Add a place')}
          </button>
          <AnimatePresence data-id="element-3802" data-mp="1bpyhlfcdsnlx">
            {menuOpen && <motion.ul role="menu" initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.12
        }} className="absolute left-0 z-30 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1" data-id="element-3803" data-mp="16thkhvn7eywp">
                {modes.map(m => {
            const Icon = m.icon;
            return <li key={m.id} data-id="element-3804" data-mp="2n6ogedeh6d5i">
                      <button type="button" role="menuitem" onClick={() => {
                setMenuOpen(false);
                setMode(m.id);
                if (m.id === 'area') onStartDrawing();
              }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-indigo-50 text-left" data-id="element-3805" data-mp="2n7pt0mb537pc">
                        <Icon className="w-4 h-4 text-slate-400" data-id="element-3806" data-mp="1dabarjge3anu" />
                        {m.label}
                      </button>
                    </li>;
          })}
              </motion.ul>}
          </AnimatePresence>
        </div> : mode === null ? <div className="flex flex-wrap items-center gap-1" data-id="element-3807" data-mp="18csz3lazmhc5">
          {places.length === 0 && <span className="text-sm text-slate-500 mr-1" data-id="element-3808" data-mp="2xs93yohvpgw8">{t('Ajouter :', 'Add:')}</span>}
          {modes.map(m => {
        const Icon = m.icon;
        return <button key={m.id} type="button" onClick={() => {
          setMode(m.id);
          if (m.id === 'area') onStartDrawing();
        }} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-[#4F46E5] rounded-lg hover:bg-indigo-50 transition-colors" data-id="element-3809" data-mp="r3qp1m3vnnxm">
                <PlusIcon className="w-3.5 h-3.5" data-id="element-3810" data-mp="19sdp1q775n4c" />
                <Icon className="w-3.5 h-3.5 opacity-70" aria-hidden="true" data-id="element-3811" data-mp="19uzzbxlh2gq9" />
                {m.label}
              </button>;
      })}
        </div> : <motion.div initial={{
      opacity: 0,
      y: -4
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.15
    }} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-id="element-3812" data-mp="1jqunt9cdn60y">
          <div className="flex items-center justify-between" data-id="element-3813" data-mp="3gjwfddoucla1">
            <span className="text-sm font-medium text-slate-700" data-id="element-3814" data-mp="3glh8qzzf5y6b">
              {mode === 'sector' ? t('Choisir des secteurs', 'Pick districts') : mode === 'address' ? t('Ajouter une adresse', 'Add an address') : t('Tracer une zone', 'Trace an area')}
            </span>
            <button type="button" onClick={closeAdder} aria-label={t('Fermer', 'Close')} className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-white transition-colors" data-id="element-3815" data-mp="1m5mjkml3jh45">
              <XIcon className="w-4 h-4" data-id="element-3816" data-mp="2eutrgc6fxj22" />
            </button>
          </div>

          {mode === 'sector' && <div className="flex flex-wrap gap-2" data-id="element-3817" data-mp="2a18p67xte2x2">
              {citySectors.map(s => {
          const active = selectedSectors.includes(s.id);
          return <button key={s.id} type="button" onClick={() => onToggleSector(s.id)} aria-pressed={active} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-white border-[#4F46E5] text-[#4F46E5]' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-3818" data-mp="32kxizl6ctscp">
                    <span className="w-2 h-2 rounded-full" style={{
              backgroundColor: s.color
            }} data-id="element-3819" data-mp="3p0xv9xnqtkw4" />
                    {s.label[lang]}
                    {active && <CheckIcon className="w-3.5 h-3.5" data-id="element-3820" data-mp="3t06o90ayleqm" />}
                  </button>;
        })}
            </div>}

          {mode === 'address' && <div className="flex flex-col sm:flex-row gap-2" data-id="element-3821" data-mp="6l3sbo08xd6n">
              <div className="relative flex-1" ref={boxRef} data-id="element-3822" data-mp="blroeo3c0k7q">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3823" data-mp="bjf0zvet1hna" />
                <input type="text" autoFocus value={query} onChange={e => {
            setQuery(e.target.value);
            setPicked(null);
            setOpen(true);
          }} onFocus={() => setOpen(true)} onKeyDown={e => {
            if (e.key === 'Enter' && picked) {
              e.preventDefault();
              addAddress();
            }
          }} placeholder={t('Rechercher une adresse…', 'Search for an address…')} aria-label={t('Rechercher une adresse', 'Search for an address')} className="w-full bg-white border border-slate-200 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-3824" data-mp="gii3b83eh9rk" />
                {open && matches.length > 0 && <ul className="absolute z-[1100] mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto py-1" data-id="element-3825" data-mp="2geptgt4fylo8">
                    {matches.map(a => <li key={a.label} data-id="element-3826" data-mp="10t5h0x8v2eva">
                        <button type="button" onClick={() => {
                setPicked(a);
                setQuery(a.label);
                setOpen(false);
              }} className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-slate-700 hover:bg-indigo-50" data-id="element-3827" data-mp="10w9r2b90c4tv">
                          <MapPinIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-3828" data-mp="37873o8jrhuy" />
                          {a.label}
                        </button>
                      </li>)}
                  </ul>}
              </div>
              <div className="relative sm:w-52" data-id="element-3829" data-mp="1xiwory65xyj1">
                <select value={radius} onChange={e => setRadius(e.target.value)} aria-label={t('Portée', 'Reach')} className="w-full bg-white border border-slate-200 rounded-lg pl-3 pr-9 py-2 text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-3830" data-mp="22irdpw6doe1g">
                  <option value="exact" data-id="element-3831" data-mp="3knz33zw9lvpf">{t('Cette adresse seulement', 'This address only')}</option>
                  {RADIUS_OPTIONS.map(r => <option key={r.value} value={r.value} data-id="element-3832" data-mp="3fq61lk5wppvh">
                      {t('Rayon de ', 'Radius of ')}
                      {r.label[lang]}
                    </option>)}
                </select>
                <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-3833" data-mp="31woddm29zoz4" />
              </div>
              <button type="button" onClick={addAddress} disabled={!picked || picked !== null && isAddressUsed(picked.label)} className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-3834" data-mp="2wx239lhquouj">
                <PlusIcon className="w-4 h-4" data-id="element-3835" data-mp="1hcjaujl5qevg" /> {t('Ajouter', 'Add')}
              </button>
            </div>}

          {mode === 'area' && <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-3836" data-mp="oqrmc90is84r">
              <p className="text-sm text-slate-500" data-id="element-3837" data-mp="tojmfqbbj0k1">
                {t(`Cliquez sur la carte — ${draftPoints.length} point(s), 3 minimum.`, `Click the map — ${draftPoints.length} point(s), 3 minimum.`)}
              </p>
              <button type="button" onClick={() => {
          onFinishDrawing();
          setMode(null);
        }} disabled={draftPoints.length < 3} className="px-3 py-1.5 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-3838" data-mp="12j7nt5drvy3k">
                {t('Ajouter la zone', 'Add area')}
              </button>
            </div>}
        </motion.div>}
    </div>;
}